// Displays the AI-detected early-payment intent with matched installment,
// amount, user's available cash, and (if insufficient) a source picker.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Zap, AlertTriangle, PiggyBank, HandCoins, TrendingUp } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAccounts } from "@/lib/useAccounts";
import { payCustomInstallment } from "@/lib/installmentsApi";
import { refreshInstallments } from "@/lib/useInstallmentsCache";
import { EarlyPaymentAINoMatch } from "@/components/installments/EarlyPaymentAINoMatch";

function fmt(n) { return `${Math.round(Number(n || 0)).toLocaleString()} AED`; }
const SOURCES = [{ k: "savings", I: PiggyBank }, { k: "revenue", I: TrendingUp }, { k: "borrow", I: HandCoins }];

export function EarlyPaymentAIModal({ open, transcript, previewData, onClose, onSaved }) {
  const { t, lang } = useI18n();
  const { items: accounts = [] } = useAccounts();
  const [source, setSource] = useState(null);
  const [accId, setAccId] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejectMsg, setRejectMsg] = useState(null);
  useEffect(() => { if (open && accounts.length === 1) setAccId(accounts[0].id); }, [open, accounts]);
  if (!open || !previewData) return null;
  const { matched, stated_name, amount, remaining, full_payoff, available, sufficient, candidates } = previewData;
  const AR = lang === "ar";

  if (!matched) return <EarlyPaymentAINoMatch statedName={stated_name} candidates={candidates} onClose={onClose} />;

  const canSubmit = !busy && amount > 0 && !!accId && (sufficient || source);
  const submit = async () => {
    setBusy(true); setRejectMsg(null);
    try { await payCustomInstallment(matched.id, amount, undefined, source, accId);
      toast.success(t("paymentRecorded")); refreshInstallments(); onSaved?.(); onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "insufficient_savings") setRejectMsg(t("rejectSavings").replace("{n}", Math.round(detail.savings).toLocaleString()));
      else if (detail?.code === "insufficient_funds") setRejectMsg(t("rejectFunds").replace("{n}", Math.round(detail.available).toLocaleString()));
      else toast.error(t("error"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="early-ai-modal" className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-md grid place-items-center p-4">
      <div className="w-full max-w-sm neu rounded-3xl p-5 space-y-3 max-h-[92vh] overflow-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl grid place-items-center bg-emerald-100 text-emerald-600"><Zap className="w-5 h-5" /></div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-extrabold text-base neu-accent-blue">{t("earlyPayment")}</p>
            {transcript && <p className="text-[10px] text-slate-500 italic truncate">&quot;{transcript}&quot;</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500"><X className="w-4 h-4" /></button>
        </div>
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
          <div className="flex justify-between"><span className="text-slate-500">{t("installment")}</span><b>{matched.name}</b></div>
          <div className="flex justify-between"><span className="text-slate-500">{full_payoff ? t("payoff") : t("amount")}</span><b className="text-red-600">{fmt(amount)}</b></div>
          <div className="flex justify-between"><span className="text-slate-500">{t("remainingPayments")}</span><span>{fmt(remaining)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">{t("available")}</span>
            <b className={available >= amount ? "text-emerald-600" : "text-red-600"}>{fmt(available)}</b></div>
        </div>
        {amount > 0 && (() => {
          const drain = (!source || source === "savings" || source === "as_is") ? amount : 0;
          const after = available - drain, bad = after < 0;
          return (
            <div data-testid="impact-preview" className={`rounded-2xl border p-3 text-[11px] flex items-center justify-between ${bad ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
              <span className="font-bold text-slate-600">{t("afterThis")}</span>
              <span className="tabular-nums"><b>{fmt(available)}</b> → <b className={bad ? "text-red-700" : "text-emerald-700"}>{fmt(after)}</b></span>
            </div>
          );
        })()}
        {!sufficient && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 space-y-2">
            <p className="text-[11px] font-bold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{t("insufficientAsk")}</p>
            <div className="grid grid-cols-3 gap-2">
              {SOURCES.map(({ k, I }) => (
                <button key={k} data-testid={`source-${k}`} onClick={() => { setSource(k); setRejectMsg(null); }}
                  className={`p-2 rounded-xl text-[11px] font-semibold flex flex-col items-center gap-1 transition ${source === k ? "bg-primary text-white shadow-lg" : "bg-white text-slate-700 border border-slate-200"}`}>
                  <I className="w-4 h-4" /> {t(`src_${k}`)}
                </button>
              ))}
            </div>
          </div>
        )}
        {rejectMsg && <div data-testid="reject-msg" className="rounded-2xl bg-red-50 border border-red-300 p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" /><p className="text-[12px] font-semibold text-red-700">{rejectMsg}</p>
        </div>}
        <select data-testid="early-ai-account" value={accId} onChange={(e) => setAccId(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-border bg-white/70 text-sm focus:border-primary focus:outline-none">
          <option value="">{AR ? "— اختر حساب الدفع" : "— Select account"}</option>
          {accounts.map((ac) => <option key={ac.id} value={ac.id}>{ac.name} · {ac.currency}</option>)}
        </select>
        <button data-testid="early-ai-confirm" disabled={!canSubmit} onClick={submit}
          className="w-full py-3 rounded-2xl neu-fill-blue text-white font-semibold shadow-lg disabled:opacity-50">{busy ? "…" : t("confirmSave")}</button>
      </div>
    </div>
  );
}
